import { useCallback, useEffect, useRef } from "react";
import { useInputFocusedStore } from "../../Presentation/Components/Modals/CommentFormModal/useInputFocusedStore";

const TEXT_INPUT_ERROR_VALIDATION_DELAY = 2000;
export type ShowErrorType = "maxLimit" | "minLimit";

export function useDeferredErrorText({
    active,
    hideErrorCallback,
    minLimit = 2,
    showErrorCallback,
    textInput,
    maxLimit = undefined,
}: {
    active: boolean;
    hideErrorCallback: () => void;
    maxLimit?: number;
    minLimit?: number;
    showErrorCallback: (errorType: ShowErrorType) => void;
    textInput: string;
}) {
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { inputFocused: isFocused } = useInputFocusedStore();

    const clearTimeoutRef = useCallback(() => {
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
            errorTimeoutRef.current = null;
        }
    }, [errorTimeoutRef.current]);

    useEffect(() => {
        if (!active) {
            return;
        }

        if (!textInput.length || (textInput.length >= minLimit && !maxLimit)) {
            hideErrorCallback();
            clearTimeoutRef();
            return;
        }

        if (
            maxLimit &&
            textInput.length <= maxLimit &&
            textInput.length >= minLimit
        ) {
            hideErrorCallback();
            clearTimeoutRef();
            return;
        }

        if (!isFocused) {
            clearTimeoutRef();

            showErrorCallback(
                maxLimit && textInput.length > maxLimit
                    ? "maxLimit"
                    : "minLimit",
            );
            return;
        }

        errorTimeoutRef.current = setTimeout(() => {
            showErrorCallback(
                maxLimit && textInput.length > maxLimit
                    ? "maxLimit"
                    : "minLimit",
            );
        }, TEXT_INPUT_ERROR_VALIDATION_DELAY);

        return () => {
            clearTimeoutRef();
        };
    }, [
        isFocused,
        showErrorCallback,
        hideErrorCallback,
        textInput,
        minLimit,
        maxLimit,
        clearTimeoutRef,
        active,
    ]);
}
